import React from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCan } from '@pankod/refine-core'

import { isSindaraOrigin } from '@resources/lms-management/types'
import BulkImportParticipantDrawer, {
  useBulkImportParticipantDrawer,
} from '../../../../program-setup/blocks/bulk-import-participant/BulkImportParticipantDrawer'
import CertificateParticipantFlagUploadButton from '../../../CertificateParticipantFlagUploadButton'
import ParticipantMapperUploadButton from '../../../ParticipantMapperUploadButton'
import ProgramVerificationDrawer, {
  useProgramVerificationDrawer,
} from '../../../../program-setup/blocks/program-verification/ProgramVerificationDrawer'
import SyncClassesButton from '../../../../program-setup/blocks/SyncClassesButton'
import useCreateDasusOffline from '../../../../program-setup/blocks/useCreateDasusOffline'
import { Highlighter } from '../../utils'

import type {
  TConfigurationSection,
  TConfigurationSectionContext,
} from '../types'

export const useOtherConfiguration = ({
  hideAddParticipantActive,
  isOwner,
  program,
  programId,
  runAndClose,
  search,
}: TConfigurationSectionContext): TConfigurationSection => {
  const { doCreate: createProgramDasus, isLoading: isLoadingProgramDasus } =
    useCreateDasusOffline(programId)
  const { show: showBulkImport, ...bulkImportDrawerProps } =
    useBulkImportParticipantDrawer()
  const { show: showVerification, ...verificationDrawerProps } =
    useProgramVerificationDrawer()
  const { data: canConfigureOfflineDasus } = useCan({
    action: 'show',
    resource: 'dasus-management',
  })
  const { data: canSyncClass } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'sync-classes' },
  })
  const { data: canUpdateCertificateFlag } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'certificate-participant-flag' },
  })
  const { data: canUploadParticipantMapper } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'participant-mapper' },
  })
  const isSindaraProgram = isSindaraOrigin(program?.origin)

  return {
    menuItems: [
      {
        type: 'group',
        label: <Highlighter search={search} text="Konfigurasi Lainnya" />,
        key: 'Konfigurasi Lainnya',
        children: [
          ...(canConfigureOfflineDasus?.can || isOwner
            ? [
                {
                  icon: <Icons.SettingOutlined />,
                  label: (
                    <Highlighter
                      search={search}
                      text="Konfigurasi Program Dasus"
                    />
                  ),
                  key: 'Konfigurasi Program Dasus',
                  onClick: () => runAndClose(() => createProgramDasus()),
                  disabled: isLoadingProgramDasus,
                },
              ]
            : []),
          {
            icon: <Icons.SyncOutlined />,
            label: (
              <SyncClassesButton disabled={!(canSyncClass?.can || isOwner)}>
                <Highlighter search={search} text="Sinkronisasi Kelas" />
              </SyncClassesButton>
            ),
            key: 'Sinkronisasi Kelas',
            onClick: () => {},
            disabled: !(canSyncClass?.can || isOwner),
          },
          {
            icon: <Icons.FlagOutlined />,
            label: (
              <CertificateParticipantFlagUploadButton
                disabled={!(canUpdateCertificateFlag?.can || isOwner)}
              >
                <Highlighter
                  search={search}
                  text="Unggah CSV Flag Sertifikat"
                />
              </CertificateParticipantFlagUploadButton>
            ),
            key: 'Unggah CSV Flag Sertifikat',
            onClick: () => {},
            disabled: !(canUpdateCertificateFlag?.can || isOwner),
          },
          {
            label: (
              <ParticipantMapperUploadButton
                disabled={!(canUploadParticipantMapper?.can || isOwner)}
                programId={programId}
              >
                {(loading) => (
                  <div className="flex gap-1.5 items-center">
                    {loading ? (
                      <Icons.LoadingOutlined />
                    ) : (
                      <Icons.PartitionOutlined />
                    )}
                    <Highlighter
                      search={search}
                      text="Unggah Participant Mapper"
                    />
                  </div>
                )}
              </ParticipantMapperUploadButton>
            ),
            key: 'Unggah Participant Mapper',
            onClick: () => {},
            disabled: !(canUploadParticipantMapper?.can || isOwner),
          },
          ...(!isSindaraProgram
            ? [
                {
                  icon: <Icons.UsergroupAddOutlined />,
                  label: (
                    <Highlighter
                      search={search}
                      text="Bulk Import Partisipan"
                    />
                  ),
                  key: 'Bulk Import Partisipan',
                  disabled: hideAddParticipantActive,
                  onClick: () => runAndClose(() => showBulkImport(programId)),
                },
              ]
            : []),
          {
            icon: <Icons.AuditOutlined />,
            label: (
              <Highlighter
                search={search}
                text="Informasi Verifikasi Program"
              />
            ),
            key: 'Informasi Verifikasi Program',
            onClick: () => runAndClose(() => showVerification(programId)),
          },
        ],
      },
    ],
    overlays: (
      <>
        {!isSindaraProgram ? (
          <BulkImportParticipantDrawer {...bulkImportDrawerProps} />
        ) : null}
        <ProgramVerificationDrawer {...verificationDrawerProps} />
      </>
    ),
  }
}
